import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Box, Chip, Link, Stack, Tooltip, Typography } from '@mui/material';
import type { MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router';
import { PATH } from '@siloreal:app:config';
import { useSilobagListContext } from '@siloreal:modules:private/silobag-list/config/context';
import UTILS from '@siloreal:modules:private/silobag-list/config/extra/utils';
import { ColDef, SilobagRow } from '@siloreal:modules:private/silobag-list/config/extra/types';
import { useRouteController } from '@siloreal:shared:hooks/controller/route/use-route-controller';
import { useLocale } from '@siloreal:shared:hooks';

const ACTIVE_BADGE_COLOR = '#a85ce3';
const EXPIRED_BADGE_COLOR = '#9E9E9E';
const VISIBLE_USER_TAGS = 3;

const USER_TAG_CHIP_SX = {
  height: 20,
  borderRadius: 1.5,
  fontSize: 12,
  lineHeight: 2,
  '& .MuiChip-label': {
    px: 1,
  },
} as const;

type UserTag = SilobagRow['tags_silobags'][number]['tag'];

function getTagLabel(tag: UserTag): string {
  return tag.translations.find((translation) => translation.language === 'es')?.value ?? tag.label;
}

function UserTagChip({ tag }: { tag: UserTag }) {
  return (
    <Chip
      label={getTagLabel(tag)}
      size="small"
      sx={{
        ...USER_TAG_CHIP_SX,
        backgroundColor: tag.bg_color,
        color: tag.text_color,
      }}
    />
  );
}

function MoreUserTagsHint({ tags }: { tags: UserTag[] }) {
  return (
    <Tooltip
      placement="right"
      arrow
      title={
        <Stack gap={0.75} alignItems="flex-start" sx={{ maxHeight: 240, overflowY: 'auto', py: 0.25 }}>
          {tags.map((tag) => (
            <UserTagChip key={tag.id} tag={tag} />
          ))}
        </Stack>
      }
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#F4F5F7',
            color: '#1F2937',
            border: '1px solid #3F3F46',
            borderRadius: '8px',
            boxShadow: '0 8px 22px rgba(17, 24, 39, 0.18)',
            p: 1,
            maxWidth: 'none',
          },
        },
        arrow: {
          sx: {
            color: '#F4F5F7',
            '&::before': {
              backgroundColor: '#F4F5F7',
              border: '1px solid #3F3F46',
              boxSizing: 'border-box',
            },
          },
        },
      }}
    >
      <Box
        component="span"
        aria-label={`${tags.length} etiquetas más`}
        onMouseDown={(event) => event.preventDefault()}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 20,
          minWidth: 20,
          px: 0.75,
          borderRadius: 1.5,
          border: '1px solid #6B7280',
          background: 'linear-gradient(180deg, #FAFBFC 0%, #EEF0F3 100%)',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.8)',
          color: '#374151',
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
          cursor: 'default',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          flexShrink: 0,
          '&:hover': {
            borderColor: '#374151',
            background: 'linear-gradient(180deg, #F3F4F6 0%, #E5E7EB 100%)',
          },
        }}
      >
        +{tags.length}
      </Box>
    </Tooltip>
  );
}

function NameLink({ row }: { row: SilobagRow }) {
  const { navigate } = useRouteController<
    Record<string, never>,
    { pathSilobags: boolean; siloListId?: string[] }
  >({});
  const {
    async: {
      list: { current: list },
    },
  } = useSilobagListContext();
  const destination = `${PATH.SILOBAG}/${row.id}`;

  const visitSilobag = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate.visit(destination, {
      state: { pathSilobags: true, siloListId: list?.data.map((item) => item.id) },
    });
  };

  return (
    <Link
      component={RouterLink}
      to={destination}
      onClick={visitSilobag}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 0,
        textDecoration: 'none',
        color: 'inherit',
        transition: 'color 0.1s ease-in-out',
        '&:hover': {
          cursor: 'pointer',
          color: '#22A7F0',
        },
      }}
    >
      <Typography fontSize={14} lineHeight={1.25} noWrap sx={{ minWidth: 0 }}>
        {row.name}
      </Typography>
      <ChevronRightIcon sx={{ fontSize: 16, color: '#22A7F0', ml: 0.5, flexShrink: 0 }} />
    </Link>
  );
}

export function buildNameColumn(): ColDef {
  return {
    field: 'name',
    headerName: useLocale.getState().t('temp.common.name'),
    minWidth: 333,
    flex: 1.2,
    renderCell: ({ row }) => {
      const tags = {
        user: row.tags_silobags.filter((t) => t.tag.scope.value !== 'system'),
        system: row.tags_silobags.filter(
          (t) => t.tag.scope.value === 'system' && !UTILS.is.vrr.ok(t.tag) && !UTILS.is.vrr.not_ok(t.tag)
        ),
      };
      const hasTagForInsurance = UTILS.has.tag.insurance.active(row);
      const hasTagForWarranty = UTILS.has.tag.warranty.active(row);
      const hasTagForInsuranceExpired = UTILS.has.tag.insurance.expired(row);
      const hasTagForWarrantyExpired = UTILS.has.tag.warranty.expired(row);
      const showWarrantyBadge = hasTagForWarranty || hasTagForWarrantyExpired;
      const showInsuranceBadge = hasTagForInsurance || hasTagForInsuranceExpired;

      return (
        <Stack
          width="100%"
          height="100%"
          pt={tags.user.length > 0 ? 0.75 : 2.25}
          justifyContent="flex-start"
          alignItems="flex-start"
          gap={0.25}
          sx={{ minWidth: 0, alignSelf: 'flex-start' }}
        >
          <Stack
            width="100%"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={0.75}
            sx={{ minWidth: 0, height: '24px' }}
          >
            <NameLink row={row} />
            <Stack direction="row" gap={0.75} flexShrink={0}>
              {showWarrantyBadge ? (
                <Tooltip
                  title={hasTagForWarrantyExpired ? useLocale.getState().t('temp.common.expired') : 'Warrant'}
                  placement="top"
                  arrow
                >
                  <Chip
                    label="W"
                    size="small"
                    icon={
                      <WorkspacePremiumIcon color="primary" sx={{ color: '#FFF', width: 14, height: 14 }} />
                    }
                    sx={{
                      height: 22,
                      flexShrink: 0,
                      borderRadius: 2,
                      backgroundColor: hasTagForWarrantyExpired ? EXPIRED_BADGE_COLOR : ACTIVE_BADGE_COLOR,
                      color: 'white',
                      fontSize: 12,
                      lineHeight: 1,
                      '& .MuiChip-label': {
                        px: 1,
                      },
                    }}
                  />
                </Tooltip>
              ) : null}
              {showInsuranceBadge ? (
                <Tooltip
                  title={
                    hasTagForInsuranceExpired ? useLocale.getState().t('temp.common.expired') : 'SiloSeguro'
                  }
                  placement="top"
                  arrow
                >
                  <Chip
                    label="SS"
                    size="small"
                    icon={<VerifiedUserIcon color="primary" sx={{ color: '#FFF', width: 14, height: 14 }} />}
                    sx={{
                      height: 22,
                      flexShrink: 0,
                      padding: 0.25,
                      borderRadius: 2,
                      backgroundColor: hasTagForInsuranceExpired ? EXPIRED_BADGE_COLOR : ACTIVE_BADGE_COLOR,
                      color: 'white',
                      fontSize: 12,
                      lineHeight: 1,
                      '& .MuiChip-label': {
                        px: 1,
                      },
                    }}
                  />
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>

          {tags.user.length > 0 ? (
            <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ maxWidth: '100%' }}>
              {tags.user.slice(0, VISIBLE_USER_TAGS).map(({ tag }) => (
                <UserTagChip key={tag.id} tag={tag} />
              ))}
              {tags.user.length > VISIBLE_USER_TAGS ? (
                <MoreUserTagsHint tags={tags.user.slice(VISIBLE_USER_TAGS).map(({ tag }) => tag)} />
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      );
    },
  };
}
